import { AUDIENCE_CARD_TYPE, AudienceAddProps, UserStatus } from '@/types/registration.types';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import { Activity, Dispatch, SetStateAction, useState } from 'react';
import { TGlobalContainerDesigns } from '@styles/design-system/global-components/global-components.types';
import Checkbox from '@ui/checkbox/checkbox';
import { ICONS } from '@ui/icons/types';
import RotatableIcon from '@ui/RotatableIcon/rotatable-icon';
import { getDisplayName } from '../../utils';

type AudienceAddRowProps = {
  audience: AudienceAddProps;
  onAddAudience?: Dispatch<SetStateAction<number>>;
  onAddRegistrantList?: (projectUuid: string, audienceTypes: AUDIENCE_CARD_TYPE[]) => void;
  audienceSelected?: boolean;
  selectedRegistrantLists: string[];
};

const DROPDOWN_SELECTS_OPTIONS: { label: string; value: AUDIENCE_CARD_TYPE; countKey: UserStatus }[] = [
  { label: 'Registered', value: AUDIENCE_CARD_TYPE.REGISTERED, countKey: 'registered' },
  { label: 'Attended', value: AUDIENCE_CARD_TYPE.ATTENDED, countKey: 'attended' },
  { label: 'No Show', value: AUDIENCE_CARD_TYPE.NO_SHOWS, countKey: 'no_shows' },
];

const AudienceAddRow = ({
  audience,
  onAddAudience,
  onAddRegistrantList,
  audienceSelected,
  selectedRegistrantLists = [],
}: AudienceAddRowProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Container className="flex flex-column radius-8 padding-h-14" design={TGlobalContainerDesigns.GlobalCont2}>
      <Container className="flex-row flex jc-space-between padding-v-14" height="48px">
        <Container className="flex-row flex ai-center gap-24">
          <Activity mode={audience.audienceType === AUDIENCE_CARD_TYPE.PASSCODE_LIST ? 'visible' : 'hidden'}>
            <Checkbox
              checked={!!audienceSelected}
              onChange={() => {
                if (audience.audienceType === AUDIENCE_CARD_TYPE.PASSCODE_LIST && audience.passcodeListId) {
                  onAddAudience?.(audience.passcodeListId);
                }
              }}
              value={audience.passcodeListId || audience.project_uuid + '-' + audience.audienceType}
              selectedIconName={ICONS.ROUND_CHECKBOX_SELECTED}
              unselectedIconName={ICONS.ROUND_CHECKBOX_UNSELECTED}
              color="var(--global-ico-text-1)"
            />
          </Activity>

          <Container className="overflow-hidden flex fd-row jc-flex-start gap-6" width="auto" background="transparent">
            <Text clamp={1} wordBreak="break-all" className="S1" maxWidth="260px">
              {getDisplayName(audience?.name)}{' '}
              {audience?.description && audience?.cloned_source ? `- ${audience?.description}` : ''}
            </Text>
            <Text clamp={1} wordBreak="break-all" className="S1" design="GlobalIcoText3">
              •
            </Text>
            <Text clamp={1} wordBreak="break-all" className="S1" design="GlobalIcoText3">
              {audience?.audienceType === AUDIENCE_CARD_TYPE.PASSCODE_LIST
                ? audience?.member_count?.toLocaleString()
                : audience?.registrant_counts?.total}{' '}
              Profiles
            </Text>
          </Container>
        </Container>
        <Activity mode={audience.audienceType === AUDIENCE_CARD_TYPE.ALL_PROFILES ? 'visible' : 'hidden'}>
          <Button className="clear-button" onClick={() => setIsOpen(!isOpen)}>
            <RotatableIcon name={ICONS.KEYBOARD_ARROW_DOWN} size={16} rotate={isOpen ? '180deg' : '0'} />
          </Button>
        </Activity>
      </Container>
      <div
        style={{
          display: 'grid',
          gridTemplateRows: isOpen ? '1fr' : '0fr',
          transition: 'grid-template-rows 200ms ease',
        }}
      >
        <div style={{ overflow: 'hidden' }}>
          <Container className="padding-l-20">
            {DROPDOWN_SELECTS_OPTIONS.map((option) => {
              return (
                <Activity
                  key={option.value}
                  mode={(audience?.registrant_counts?.[option.countKey] ?? 0) === 0 ? 'hidden' : 'visible'}
                >
                  <Container className="flex-row flex ai-center gap-24 padding-v-14">
                    <Checkbox
                      checked={selectedRegistrantLists.includes(option.value)}
                      onChange={() => {
                        if (!audience.project_uuid) return;
                        const isSelecting = !selectedRegistrantLists.includes(option.value);
                        onAddRegistrantList?.(audience.project_uuid, [option.value]);
                        if (isSelecting) {
                          if (option.value === AUDIENCE_CARD_TYPE.REGISTERED) {
                            [AUDIENCE_CARD_TYPE.ATTENDED, AUDIENCE_CARD_TYPE.NO_SHOWS].forEach((t) => {
                              if (selectedRegistrantLists.includes(t)) {
                                onAddRegistrantList?.(audience.project_uuid!, [t]);
                              }
                            });
                          } else {
                            if (selectedRegistrantLists.includes(AUDIENCE_CARD_TYPE.REGISTERED)) {
                              onAddRegistrantList?.(audience.project_uuid!, [AUDIENCE_CARD_TYPE.REGISTERED]);
                            }
                          }
                        }
                      }}
                      value={audience.project_uuid + '-' + option.value}
                      selectedIconName={ICONS.ROUND_CHECKBOX_SELECTED}
                      unselectedIconName={ICONS.ROUND_CHECKBOX_UNSELECTED}
                      color="var(--global-ico-text-1)"
                    />
                    <Container
                      className="overflow-hidden flex fd-row jc-flex-start gap-6"
                      width="auto"
                      background="transparent"
                    >
                      <Text clamp={1} wordBreak="break-all" className="S1" maxWidth="260px">
                        {option.label}
                      </Text>
                      <Text clamp={1} wordBreak="break-all" className="S1" design="GlobalIcoText3">
                        •
                      </Text>
                      <Text clamp={1} wordBreak="break-all" className="S1" design="GlobalIcoText3">
                        {audience?.registrant_counts?.[option.countKey] ?? 0} Profiles
                      </Text>
                    </Container>
                  </Container>
                </Activity>
              );
            })}
          </Container>
        </div>
      </div>
    </Container>
  );
};

export default AudienceAddRow;
